import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { Container } from "@roqua/components";
import { Section } from "@roqua/components/sections";
import { Button } from "@roqua/components/ui/button";
import { FieldLegend, FieldSet } from "@roqua/components/ui/field";

import { FieldDescription } from "@/components/forms/FieldDescription";
import { PageTitle } from "@/components/PageTitle";
import { useAppForm } from "@/hooks/useAppForm";

export const Route = createFileRoute("/$organizationKey/$dossierId/design/forms")({
  component: RouteComponent,
});

const formSchema = z.object({
  name: z.string(),
  email: z.email(),
  long: z.string(),
  radio: z.enum(["one", "two", "three"], {}),
  check: z.boolean(),
  checks: z.array(z.string()).min(1, {
    message: "You have to select at least one item.",
  }),
  select: z.enum(["apple", "pear", "grape"]),
  date1: z.date(),
  date2: z.date(),
});

function RouteComponent() {
  const form = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      radio: "one",
      select: undefined as string | undefined,
      check: false,
      checks: [] as string[],
      date1: undefined as Date | undefined,
      date2: new Date(),
      long: "",
    },
    validators: { onSubmit: formSchema },
    onSubmit: async (data) => {
      toast("Submitted", {
        description: (
          <pre className="mt-2 w-[320px] rounded-md bg-neutral-950 p-4">
            <code className="text-white">{JSON.stringify(data, null, 2)}</code>
          </pre>
        ),
      });
    },
  });

  return (
    <>
      <PageTitle title="designForms" />
      <Section>
        <div className="">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="space-y-4"
          >
            <Container>
              <FieldSet>
                <FieldLegend>Inputs</FieldLegend>
                <FieldDescription>Simpele invoervelden</FieldDescription>

                <form.AppField name="name">
                  {(field) => <field.TextField label="Naam" />}
                </form.AppField>

                <form.AppField name="email">
                  {(field) => <field.TextField label="Email" type="email" />}
                </form.AppField>
                <form.AppField name="long">
                  {(field) => (
                    <field.TextareaField label="Opmerkingen" description="uitleg over dit veld" />
                  )}
                </form.AppField>
              </FieldSet>
            </Container>

            <Container>
              <FieldSet>
                <FieldLegend>shadcn radios</FieldLegend>
                <form.AppField name="radio">
                  {(field) => (
                    <field.RadioGroupField
                      label="Radio group"
                      options={[
                        { label: "Radio One", value: "one" },
                        { label: "Radio Two", value: "two" },
                        { label: "Radio Three", value: "three" },
                      ]}
                    />
                  )}
                </form.AppField>

                <form.AppField name="radio">
                  {(field) => (
                    <field.RadioCardsField
                      label="Radio group"
                      options={[
                        { title: "Radio One", description: "Tweede regel", value: "one" },
                        { title: "Radio Two", value: "two" },
                        {
                          title: "Radio Three",
                          description: (
                            <Button onClick={(e) => e.preventDefault()}>
                              Random react mag erin
                            </Button>
                          ),
                          value: "three",
                        },
                      ]}
                    />
                  )}
                </form.AppField>
              </FieldSet>
            </Container>

            <Container>
              <FieldSet>
                <FieldLegend>Checkboxes</FieldLegend>
                <FieldDescription>Haal hier al je booleans</FieldDescription>
                <form.AppField name="check">
                  {(field) => <field.CheckboxField label="Ik wil graag alle spam" />}
                </form.AppField>
                <form.AppField name="checks" mode="array">
                  {(field) => (
                    <field.CheckboxGroupField
                      label="Checkbox group"
                      options={[
                        { label: "Checkbox One", value: "one" },
                        { label: "Checkbox Two", value: "two" },
                        { label: "Checkbox Three", value: "three" },
                      ]}
                    />
                  )}
                </form.AppField>
              </FieldSet>
            </Container>

            <Container>
              <FieldSet>
                <FieldLegend>select</FieldLegend>
                <form.AppField name="select">
                  {(field) => (
                    <field.SelectField
                      label="Select a fruit"
                      description="Fruits are good for you"
                      placeholder="Select a fruit"
                      options={[
                        { label: "Apple", value: "apple" },
                        { label: "Pear", value: "pear" },
                        { label: "Grape", value: "grape" },
                      ]}
                    />
                  )}
                </form.AppField>
              </FieldSet>
            </Container>

            <Container>
              <FieldSet>
                <FieldLegend>Dates</FieldLegend>
                <form.AppField name="date1">
                  {(field) => <field.DateField label="Select a date" />}
                </form.AppField>
                <form.AppField name="date2">
                  {(field) => (
                    <field.DateTimeField
                      label="Select a datetime with min/max"
                      min={new Date("2024-01-01T08:00")}
                      max={new Date("2024-12-31T18:00")}
                    />
                  )}
                </form.AppField>
              </FieldSet>
            </Container>

            <Button type="submit">Submit</Button>
          </form>
        </div>
      </Section>
    </>
  );
}
